import { gql } from '@eti/schema-types';

export const BOOKING_STEP_QUERY = gql(/* GraphQL */ `
  query getBookingStepPageData($excludedExtraProductIds: [ID], $stepNumber: Int!) {
    countries {
      id
      name
      code
      phonePrefixes {
        id
        prefix
      }
    }
    cart {
      id
    }
    travelerDetails {
      searchMetaData {
        selectionKey
        searchPath
      }
      shouldShowEmergencyContactInformation: showEmergencyContactInformation
      travelers {
        id
        shouldShowFrequentFlyerCard: showFrequentFlyerCard
        travelDocument {
          shouldShowTravelDocumentIssueDate: showTravelDocumentIssueDate
          shouldShowTravelDocumentIssueCity: showTravelDocumentIssueCity
          shouldShowTravelDocumentDateOfBirth: showTravelDocumentDateOfBirth
          shouldShowTravelDocumentNationality: showTravelDocumentNationality
          shouldShowTravelDocumentPassportNumber: showTravelDocumentPassportNumber
          shouldShowTravelDocumentCpfNumber: showTravelDocumentCpfNumber
          requireTravelDocumentExpiryDate
        }
      }
    }
    selectedTrip {
      id
      isVI
      selectionKey
      includedExtraProducts {
        id
        texts {
          name
          productSummaryAlternativeName
          readMoreText
          receiptText
          salesAbstract
        }
      }
      chargingCurrencyExchangeRate {
        currency {
          code
          exponent
          name
        }
        priceFormat {
          currencyExponent
          currencySymbol
          decimalSymbol
          groupingSeparator
          pattern
        }
        value
      }
      dynamicBookingFlow {
        stepIndicators {
          stepNumber
          name
          completed
        }
        stepProducts(stepNumber: $stepNumber) {
          availableExtraProducts(excludedExtraProducts: $excludedExtraProductIds) {
            ...AvailableExtraProductsOnTDPage
          }
          availableAncillaryBundles {
            ...AncillaryBundlesOnTDPage
          }
          mutuallyExclusiveProducts {
            productTypes
          }
        }
      }
      bounds {
        boundId: id
        includedCheckedBaggage {
          ...IncludedCheckedBaggage
        }
        includedCabinBaggage {
          ...IncludedCabinBaggage
        }
        segments {
          ... on TripSegment {
            __typename
            arrivedAt
            aircraftType
            brandedFareInformation {
              name
            }
            includedCheckedBaggage {
              ...IncludedCheckedBaggage
            }
            includedCabinBaggage {
              ...IncludedCabinBaggage
            }
            cabinClassName
            departuredAt
            destination {
              code
              name
              cityCode
              cityName
              isAirport
            }
            duration
            equipmentCode
            flightNumber
            segmentId: id
            marketingCarrier {
              code
              name
              logo {
                small
                large
              }
            }
            numberOfTechnicalStops
            technicalStops {
              arrivalAt
              departureAt
              duration
              location {
                code
                name
                cityName
                cityCode
              }
            }
            operatingCarrier {
              code
              name
              logo {
                small
                large
              }
            }
            operatingInformation
            ticketingCarrier {
              code
              name
            }
            origin {
              code
              name
              cityCode
              cityName
              isAirport
            }
            segmentDetails {
              paxType
              numberOfSeatsLeft
            }
          }
          ... on EventSegment {
            __typename
            duration
            types
            segmentId: id
          }
        }
      }
      changes {
        ... on TripPriceChange {
          id
          priceNew {
            price {
              value
              currency {
                code
              }
            }
          }
          priceOld {
            price {
              value
              currency {
                code
              }
            }
          }
        }
        ... on TripScheduleChange {
          id
          arrivalTimeNew
          arrivalTimeOld
          departureTimeNew
          departureTimeOld
          segmentId
        }
      }
      frequentFlyerCardTypes {
        id
        name
      }
      paymentMethodPrices {
        name
        id
        price {
          value
        }
        type
      }
      travelers {
        age
        ageType
        birthDate
        contactDetails {
          email
          phone {
            countryCallingCode
            numberWithoutCountryCode
          }
        }
        firstName
        gender
        lastName
        id
      }
      customer {
        phone {
          countryCallingCode
          numberWithoutCountryCode
        }
        email
        firstName
        lastName
      }
      travelerPrices {
        id
        price {
          markup {
            value
          }
          price {
            value
            currency {
              code
            }
          }
          vat {
            value
          }
        }
        taxesAndFees {
          category
          taxes {
            code
            title
            amount {
              value
            }
          }
        }
        travelerId
        carrierPriceBreakdowns {
          carrier {
            code
            conditions {
              description
              url
            }
            name
          }
          providerPrice {
            currency {
              code
              exponent
              name
            }
            value
          }
          taxesAndFees {
            category
            taxes {
              amount {
                currency {
                  code
                  exponent
                  name
                }
                value
              }
              title
              code
            }
          }
          vat {
            currency {
              code
              exponent
              name
            }
            value
          }
        }
      }
      travelerPricesWithoutPaymentDiscounts {
        price {
          markup {
            value
          }
          price {
            value
          }
          vat {
            value
          }
        }
        taxesAndFees {
          category
          taxes {
            code
            title
            amount {
              value
            }
          }
        }
        travelerId
      }
      tripCharacteristics
      checkedBaggageSummary {
        tripSummaries {
          travelerId
          baggageDetails {
            pieces
            weight
            weightUnit
          }
          baggageStatus
        }
      }
      cabinBaggageTripSummary {
        cabinBaggageTravelerDetails {
          cabinBaggageDetails {
            pieces
            weight
            weightUnit
          }
          cabinBaggageStatus
          travelerId
        }
      }
      personalItemTripSummary {
        personalItemTravelerDetails {
          personalItemStatus
          travelerId
          personalItemDetails {
            size3d {
              height
              length
              width
            }
          }
        }
      }
      includedCheckedBaggage {
        ...IncludedCheckedBaggage
      }
      includedCabinBaggage {
        ...IncludedCabinBaggage
      }
      type
      systems
      voucherAmount {
        value
      }
      shareableUrl
    }
  }
`);

export const TRAVELER_INFORMATION_QUERY = gql(/* GraphQL */ `
  query getTravelerInformationData {
    travelerInformation {
      contactInformation {
        cellPhone {
          prefixId
          phoneNumberAfterPrefix
          countryId
        }
        email
      }
      emergencyContactInformation {
        lastName
        phoneNumber {
          prefixId
          phoneNumberAfterPrefix
          countryId
        }
      }
      subscribeToNewsletter
      travelers {
        id
        gender
        ageType
        firstName
        lastName
        travelDocumentInformation {
          birthDate
          travelDocumentNumber
          expiryDate
          nationalityId
          issueDate
          issueCity
          cpfNumber
        }
        frequentFlyerCard {
          frequentFlyerCardNumber
          frequentFlyerCardId
        }
        birthDate
      }
    }
  }
`);

export const CUSTOMER_PRODUCT_SELECTION_QUERY = gql(/* GraphQL */ `
  query getCustomerProductSelection {
    customerProductSelection {
      userSelectedProductDetails {
        stepNumber
        ancillaryBundleSelection {
          bundleId
          optionId
        }
        extraProductSelection {
          productId
          selectionNone
          selectionTraveler
          selectionTravelerNumberOfUnits {
            travelerId
            numberOfUnits
          }
          selectionSeatMap {
            travelerId
            row
            column
            seatPreference
            boundIndex
            segmentIndex
            deckType
          }
          selectionTravelerChoice {
            travelerId
            choiceId
          }
          selectionCabinBaggage {
            travelerId
          }
        }
      }
      selectedProducts {
        id
        code
        name
        sellSpecification {
          ... on SellSpecificationNoneNew {
            __typename
            price {
              markup {
                value
                currency {
                  code
                }
              }
              price {
                value
                currency {
                  code
                }
              }
              vat {
                value
                currency {
                  code
                }
              }
            }
          }
          ... on SellSpecificationBaggageNew {
            __typename
            sellPriceBaggage {
              travelerId
              hasMultipleWeightOptions
              options {
                numberOfUnits
                weight
                weightUnit
                preselected
                ancillaryCarrierPriceBreakdowns {
                  carrier {
                    code
                    name
                  }
                  providerPrice {
                    value
                  }
                }
                price {
                  markup {
                    value
                  }
                  price {
                    value
                  }
                  vat {
                    value
                  }
                }
              }
            }
          }
          ... on SellSpecificationSeatingSeatMapNew {
            __typename
            sellSeatMapBounds {
              id
              sellSeatMapSegments {
                ... on SellSeatMapSegmentNew {
                  __typename
                  id
                  travelerIds
                  bestPriceCharacteristics {
                    characteristic
                    price
                  }
                  lowerDeck {
                    cabins {
                      rows {
                        characteristics
                        rowNumber
                        sellSeatMapSeats {
                          column
                          isBookable
                          price {
                            price {
                              value
                            }
                            markup {
                              value
                            }
                          }
                          row
                          seat
                          type
                          mainCharacteristic
                        }
                      }
                      sellSeatMapColumnDetails {
                        columnName
                        type
                      }
                    }
                  }
                  mainDeck {
                    cabins {
                      rows {
                        characteristics
                        rowNumber
                        sellSeatMapSeats {
                          column
                          isBookable
                          price {
                            price {
                              value
                            }
                            markup {
                              value
                            }
                          }
                          row
                          seat
                          type
                          mainCharacteristic
                        }
                      }
                      sellSeatMapColumnDetails {
                        columnName
                        type
                      }
                    }
                  }
                  upperDeck {
                    cabins {
                      rows {
                        characteristics
                        rowNumber
                        sellSeatMapSeats {
                          column
                          isBookable
                          price {
                            price {
                              value
                            }
                            markup {
                              value
                            }
                          }
                          row
                          seat
                          type
                          mainCharacteristic
                        }
                      }
                      sellSeatMapColumnDetails {
                        columnName
                        type
                      }
                    }
                  }
                }
                ... on SellSeatingPreference {
                  __typename
                  price {
                    price {
                      value
                    }
                    markup {
                      value
                    }
                  }
                  id
                }
                ... on SellSeatingBeside {
                  __typename
                  price {
                    price {
                      value
                    }
                    markup {
                      value
                    }
                  }
                  id
                }
              }
            }
          }
          ... on SellSpecificationTravelerChoiceNew {
            __typename
            availableChoices {
              id
              name
              descriptions {
                text
                type
              }
              pricePerTravelers {
                travelerId
                price {
                  price {
                    value
                  }
                }
              }
            }
            preselectedChoice
            pricePerTravelers {
              travelerId
              price {
                markup {
                  value
                  currency {
                    code
                  }
                }
                price {
                  value
                  currency {
                    code
                  }
                }
                vat {
                  value
                  currency {
                    code
                  }
                }
              }
            }
          }
          ... on SellSpecificationTravelerNew {
            __typename
            sellPriceTravelers {
              travelerId
              price {
                markup {
                  value
                  currency {
                    code
                  }
                }
                price {
                  value
                  currency {
                    code
                  }
                }
                vat {
                  value
                  currency {
                    code
                  }
                }
              }
            }
          }
          ... on SellSpecificationInsuranceNew {
            __typename
            termsUrl
            numberOfTravelers
            sellPriceInsurances {
              description
              disclaimer
              id
              inclusions {
                description
                title
              }
              persuasionEnabled
              price {
                markup {
                  currency {
                    code
                    exponent
                    name
                  }
                  value
                }
                price {
                  currency {
                    code
                    exponent
                    name
                  }
                  value
                }
                vat {
                  currency {
                    code
                    exponent
                    name
                  }
                  value
                }
              }
              title
            }
          }
          ... on SellCabinBaggageNew {
            __typename
            weight
            weightUnit
            pieces
            size3d {
              height
              length
              width
            }
            sellPriceTravelers {
              ancillaryCarrierPriceBreakdowns {
                carrier {
                  code
                  name
                }
                providerPrice {
                  currency {
                    code
                  }
                  value
                }
              }
              travelerId
              price {
                price {
                  value
                  currency {
                    code
                  }
                }
                markup {
                  value
                  currency {
                    code
                  }
                }
                vat {
                  currency {
                    code
                  }
                  value
                }
              }
            }
          }
        }
        configuration {
          travelerDetails {
            persuasionEnabled
            showPricePerBound
            showPricePerTraveler
            highlightProduct
          }
          postbookingPayment {
            persuasionEnabled
            showPricePerBound
            showPricePerTraveler
            highlightProduct
          }
          postbookingAncillaryStore {
            persuasionEnabled
            showPricePerBound
            showPricePerTraveler
            highlightProduct
          }
          productPreSelection
        }
        productSpecificTexts {
          ... on InsuranceTexts {
            termsUrl
            title
            description
            inclusions {
              title
              description
            }
            disclaimer
            ipid {
              name
              url
            }
          }
          ... on SeatMapTexts {
            seatMapSegmentTexts {
              boundIndex
              segmentIndex
              text
            }
          }
          ... on ProductSegmentAvailabilityTexts {
            __typename
            productSegmentAvailabilityTexts {
              loungeInfo {
                closeAt
                loungeName
                openAt
                terminal
                amenities {
                  code
                  id
                  name
                }
                conditions {
                  code
                  description
                }
              }
              arrivalAirport
              available
              boundIndex
              boundType
              departureAirport
              departureDateTime
              extSystem
              flightNumber
              segmentIndex
            }
          }
        }
        selectedWithTrip
        texts {
          name
          productSummaryAlternativeName
          readMoreText
          receiptText
          salesAbstract
          legalInformation
          upsellTextWhenBooked
        }
      }
      selectedAncillaryBundles {
        id
        name
        selectedOption
        options {
          id
          name
          content {
            code
            name
            productSpecificTexts {
              ... on ProductSegmentAvailabilityTexts {
                __typename
                productSegmentAvailabilityTexts {
                  arrivalAirport
                  available
                  boundIndex
                  boundType
                  departureAirport
                  departureDateTime
                  extSystem
                  flightNumber
                  segmentIndex
                  loungeInfo {
                    amenities {
                      code
                      id
                      name
                    }
                    closeAt
                    conditions {
                      code
                      description
                    }
                    loungeName
                    openAt
                    terminal
                  }
                }
              }
              ... on SeatMapTexts {
                __typename
                seatMapSegmentTexts {
                  text
                  segmentIndex
                  boundIndex
                }
              }
              ... on InsuranceTexts {
                __typename
                title
                termsUrl
                disclaimer
                description
                ipid {
                  url
                  name
                }
                inclusions {
                  title
                  description
                }
              }
            }
            description
            included
            readMoreText
            size3d {
              height
              width
              length
            }
            weight
            weightUnit
            pieces
            productTypeId
          }
          price {
            price {
              value
            }
          }
        }
      }
    }
  }
`);

export const VALIDATE_EMAIL = gql(/* GraphQL */ `
  query ValidateEmail($email: String!) {
    validateEmail(email: $email) {
      valid
      message
    }
  }
`);

export const VALIDATE_PHONE_NUMBER = gql(/* GraphQL */ `
  query ValidatePhoneNumber($phoneNumberAfterPrefix: String!, $prefixId: ID!) {
    validatePhoneNumber(phoneNumberAfterPrefix: $phoneNumberAfterPrefix, prefixId: $prefixId) {
      valid
      message
    }
  }
`);

export const ANCILLARY_ITEMS_QUERY = gql(/* GraphQL */ `
  query getAncillaryItemsOnBookingStep(
    $customerPhoneNumber: String
    $customerPhoneNumberPrefix: Int
    $productIds: [ID!]
  ) {
    ancillaryItems(
      customerPhoneNumber: $customerPhoneNumber
      customerPhoneNumberPrefix: $customerPhoneNumberPrefix
      productIds: $productIds
    ) {
      id
      name
      code
      selectedWithTrip
      productSpecificTexts {
        ... on InsuranceTexts {
          termsUrl
          title
          description
          inclusions {
            title
            description
          }
          disclaimer
          ipid {
            name
            url
          }
        }
        ... on SeatMapTexts {
          seatMapSegmentTexts {
            boundIndex
            segmentIndex
            text
          }
        }
      }
      sellSpecification {
        ... on SellSpecificationNoneNew {
          __typename
          price {
            markup {
              value
              currency {
                code
              }
            }
            price {
              value
              currency {
                code
              }
            }
            vat {
              value
              currency {
                code
              }
            }
          }
        }
        ... on SellSpecificationBaggageNew {
          __typename
          sellPriceBaggage {
            travelerId
            hasMultipleWeightOptions
            options {
              numberOfUnits
              weight
              weightUnit
              preselected
              ancillaryCarrierPriceBreakdowns {
                carrier {
                  code
                  name
                }
                providerPrice {
                  value
                }
              }
              price {
                markup {
                  value
                }
                price {
                  value
                }
                vat {
                  value
                }
              }
            }
          }
        }
        ... on SellSpecificationSeatingSeatMapNew {
          __typename
          sellSeatMapBounds {
            id
            sellSeatMapSegments {
              ... on SellSeatMapSegmentNew {
                __typename
                id
                travelerIds
                bestPriceCharacteristics {
                  characteristic
                  price
                }
                lowerDeck {
                  cabins {
                    rows {
                      characteristics
                      rowNumber
                      sellSeatMapSeats {
                        column
                        isBookable
                        price {
                          price {
                            value
                          }
                          markup {
                            value
                          }
                        }
                        row
                        seat
                        type
                        mainCharacteristic
                      }
                    }
                    sellSeatMapColumnDetails {
                      columnName
                      type
                    }
                  }
                }
                mainDeck {
                  cabins {
                    rows {
                      characteristics
                      rowNumber
                      sellSeatMapSeats {
                        column
                        isBookable
                        price {
                          price {
                            value
                          }
                          markup {
                            value
                          }
                        }
                        row
                        seat
                        type
                        mainCharacteristic
                      }
                    }
                    sellSeatMapColumnDetails {
                      columnName
                      type
                    }
                  }
                }
                upperDeck {
                  cabins {
                    rows {
                      characteristics
                      rowNumber
                      sellSeatMapSeats {
                        column
                        isBookable
                        price {
                          price {
                            value
                          }
                          markup {
                            value
                          }
                        }
                        row
                        seat
                        type
                        mainCharacteristic
                      }
                    }
                    sellSeatMapColumnDetails {
                      columnName
                      type
                    }
                  }
                }
              }
              ... on SellSeatingPreference {
                __typename
                price {
                  price {
                    value
                  }
                  markup {
                    value
                  }
                }
                id
              }
              ... on SellSeatingBeside {
                __typename
                price {
                  price {
                    value
                  }
                  markup {
                    value
                  }
                }
                id
              }
            }
          }
        }
        ... on SellSpecificationTravelerChoiceNew {
          __typename
          availableChoices {
            id
            name
            descriptions {
              text
              type
            }
            pricePerTravelers {
              travelerId
              price {
                price {
                  value
                }
              }
            }
          }
          preselectedChoice
          pricePerTravelers {
            travelerId
            price {
              markup {
                value
                currency {
                  code
                }
              }
              price {
                value
                currency {
                  code
                }
              }
              vat {
                value
                currency {
                  code
                }
              }
            }
          }
        }
        ... on SellSpecificationTravelerNew {
          __typename
          sellPriceTravelers {
            travelerId
            price {
              markup {
                value
                currency {
                  code
                }
              }
              price {
                value
                currency {
                  code
                }
              }
              vat {
                value
                currency {
                  code
                }
              }
            }
          }
        }
        ... on SellCabinBaggageNew {
          weight
          weightUnit
          pieces
          size3d {
            height
            length
            width
          }
          sellPriceTravelers {
            travelerId
            price {
              price {
                value
                currency {
                  code
                }
              }
              markup {
                value
                currency {
                  code
                }
              }
              vat {
                currency {
                  code
                }
                value
              }
            }
          }
        }
      }
      configuration {
        travelerDetails {
          persuasionEnabled
          showPricePerBound
          showPricePerTraveler
          highlightProduct
        }
        productPreSelection
      }
    }
  }
`);
